import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { money } from "../lib/format";
import {
  bookmarkletHref, isSyncPayload, onPending, takePending, type SyncCapturePayload,
} from "../lib/marketSync";

/**
 * Marketplace sync. The "Sync to Thrifty" bookmark records a shop or sold page
 * on Depop / Vinted / eBay / Poshmark and hands it here; the API lines it up
 * against inventory and nothing is written until Apply, with only the rows
 * left ticked.
 */

type Op =
  | { kind: "linkListing"; itemId: string | null; [k: string]: unknown }
  | { kind: "createSale"; itemId: string | null; [k: string]: unknown }
  | { kind: "updateListing"; listingId: string; [k: string]: unknown }
  | { kind: "updateSale"; saleId: string; [k: string]: unknown }
  | { kind: "createItem"; item: { sku: string | null }; [k: string]: unknown };

interface Applied { op: string; id: string; sku?: string; skuFromListing?: boolean }

interface Row {
  key: string;
  kind: "listing" | "sale" | "missing";
  externalId: string;
  title: string | null;
  brand: string | null;
  size: string | null;
  price: number | null;
  url: string | null;
  photoUrl: string | null;
  soldAt: string | null;
  buyer: string | null;
  basis: "linked" | "sku" | "title" | "inferred" | "none";
  confidence: number;
  itemId: string | null;
  sku: string | null;
  appTitle: string | null;
  appStatus: string | null;
  status: "tied" | "needs-change" | "unmatched" | "alert";
  changes: Array<{ field: string; from: string | number | null; to: string | number | null; reason: string }>;
  op: Op | null;
  defaultOn: boolean;
  notes: string[];
  suggestions: Array<{ itemId: string; sku: string; title: string | null; score: number }>;
  /** Unmatched rows: creates a new item from the listing instead of linking one. */
  createOp: (Op & { kind: "createItem" }) | null;
  /** Unmatched rows: "new" or a suggested item's id. */
  defaultChoice: string | null;
}

interface Preview {
  captureId: string;
  platform: string;
  pageUrl: string;
  captures: number;
  reachedEnd: boolean;
  bookmarkletOutdated: boolean;
  adapterReady: boolean;
  recognized: number;
  result: null | {
    rows: Row[];
    seenListingIds: string[];
    summary: {
      listingsSeen: number; salesSeen: number; tied: number; needsChange: number;
      unmatched: number; alerts: number; missing: number; ignored: number; newItems: number;
    };
  };
}

interface CaptureLog {
  id: string;
  platform: string;
  pageUrl: string;
  captureCount: number;
  bytes: number;
  reachedEnd: boolean;
  recognized: { listings: number; sales: number } | null;
  appliedCount: number | null;
  createdAt: string;
}

const SITES: Array<{ name: string; shop: string; sold: string }> = [
  { name: "Depop", shop: "Selling hub → Selling (depop.com/sellinghub/selling/active)", sold: "Selling hub → Sold items (depop.com/sellinghub/sold-items)" },
  { name: "Vinted", shop: "your profile's wardrobe (vinted.com/member/…)", sold: "My orders → Sold (sales are not read yet)" },
  { name: "eBay", shop: "Seller Hub → Listings → Active (not read yet)", sold: "Seller Hub → Orders (not read yet)" },
  { name: "Poshmark", shop: "your closet (poshmark.com/closet/…)", sold: "My Sales (poshmark.com/order/sales)" },
];

const needsItem = (op: Op | null) =>
  !!op && (op.kind === "linkListing" || op.kind === "createSale") && !op.itemId;

/** The op to send for a row, given what was picked for an unmatched one. */
function opFor(r: Row, choice: string | undefined): Op | null {
  if (!needsItem(r.op)) return r.op;
  if (!choice) return null;
  return choice === "new" ? r.createOp : { ...r.op!, itemId: choice } as Op;
}

/** Review tabs, one per kind of outcome, in the order they're worth reading. */
type TabKey = "new" | "sold" | "listings" | "takedown" | "gone" | "tied";
const TABS: Array<{ key: TabKey; label: string; hint: string }> = [
  {
    key: "new", label: "New to Thrifty",
    hint: "Listings with no Thrifty item. Ticked rows become new items with the listing and its photos attached. If one is really an item you already have, pick it from the list instead.",
  },
  {
    key: "sold", label: "Sold",
    hint: "Sales to record or correct, and listings the site now shows as sold. A sale of an item that isn't in Thrifty yet creates the item too.",
  },
  {
    key: "listings", label: "Listing updates",
    hint: "Listings matched to Thrifty items: new links, price changes and status changes.",
  },
  {
    key: "takedown", label: "Take down",
    hint: "Sold or written off in Thrifty, but still for sale on this site. Delist these on the site itself.",
  },
  {
    key: "gone", label: "Gone from site",
    hint: "Live in Thrifty but not among the site's active listings. Unticked by default: sync the sold page first so a sale is recorded rather than an ended listing.",
  },
  { key: "tied", label: "Already in sync", hint: "Nothing to change." },
];

function tabOf(r: Row): TabKey {
  if (r.status === "alert") return "takedown";
  if (r.kind === "missing") return "gone";
  if (r.status === "tied") return "tied";
  if (r.kind === "sale") return "sold";
  if (r.status === "unmatched") return "new";
  if (r.changes.some((c) => c.field === "status" && c.to === "sold")) return "sold";
  return "listings";
}

function describe(v: string | number | null, field: string): string {
  if (v === null || v === "") return "not set";
  if (field === "listPrice" || field === "salePrice" || field === "sale") return money(v);
  return String(v);
}

export function Sync() {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ count: number; skipped: string[]; created: Applied[] } | null>(null);
  const [photoJob, setPhotoJob] = useState<{ copied: number; remaining: number; running: boolean } | null>(null);
  const [on, setOn] = useState<Set<string>>(new Set());
  // Item picked by hand for an unmatched row.
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [log, setLog] = useState<{ captures: CaptureLog[]; adapters: string[] } | null>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  // React refuses javascript: hrefs, so the bookmark link is set directly.
  useEffect(() => {
    linkRef.current?.setAttribute("href", bookmarkletHref(window.location.origin));
  }, []);

  const loadLog = useCallback(() => {
    api.get<{ captures: CaptureLog[]; adapters: string[] }>("/sync/captures").then(setLog).catch(() => {});
  }, []);

  // Listing photos for newly created items are copied a few items per call;
  // keep calling until none are left. Safe to leave: the next visit resumes.
  const copying = useRef(false);
  const copyPhotos = useCallback(async () => {
    if (copying.current) return;
    copying.current = true;
    let copied = 0;
    try {
      for (let guard = 0; guard < 500; guard++) {
        const res = await api.post<{ items: number; copied: number; remaining: number }>("/sync/photos");
        copied += res.copied;
        setPhotoJob({ copied, remaining: res.remaining, running: res.remaining > 0 });
        if (res.remaining === 0 || res.items === 0) break;
      }
    } catch {
      setPhotoJob((j) => (j ? { ...j, running: false } : j));
    } finally {
      copying.current = false;
    }
  }, []);

  useEffect(() => { void copyPhotos(); }, [copyPhotos]);

  const run = useCallback(async (payload: SyncCapturePayload) => {
    setBusy(true);
    setError(null);
    setPreview(null);
    setDone(null);
    setPicked({});
    try {
      const res = await api.post<Preview>("/sync/preview", { payload });
      const got = res.result?.rows ?? [];
      setPreview(res);
      // Unmatched rows start on the server's pick: create new, or a close item.
      setPicked(Object.fromEntries(got.filter((r) => r.defaultChoice).map((r) => [r.key, r.defaultChoice!])));
      setOn(new Set(got.filter((r) => r.defaultOn && r.op && (!needsItem(r.op) || r.defaultChoice)).map((r) => r.key)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read that page");
    } finally {
      setBusy(false);
      loadLog();
    }
  }, [loadLog]);

  // A handoff that arrived before this page mounted (e.g. while signing in),
  // and any that arrive while it is open.
  useEffect(() => {
    const p = takePending();
    if (p) void run(p);
    return onPending(() => {
      const next = takePending();
      if (next) void run(next);
    });
  }, [run]);

  useEffect(loadLog, [loadLog]);

  async function onFile(file: File) {
    setError(null);
    try {
      const data = JSON.parse(await file.text());
      if (!isSyncPayload(data)) throw new Error("That file isn't a Thrifty sync recording.");
      await run(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read that file");
    }
  }

  const rows = preview?.result?.rows ?? [];
  const actionable = useMemo(() => rows.filter((r) => r.op), [rows]);
  const selected = useMemo(
    () => actionable.filter((r) => on.has(r.key) && (!needsItem(r.op) || picked[r.key])),
    [actionable, on, picked]
  );

  const byTab = useMemo(() => {
    const m = new Map<TabKey, Row[]>(TABS.map((t) => [t.key, []]));
    for (const r of rows) m.get(tabOf(r))!.push(r);
    return m;
  }, [rows]);
  // Open on the first tab with something to do.
  const [tab, setTab] = useState<TabKey>("new");
  useEffect(() => {
    const first = TABS.find((t) => t.key !== "tied" && (byTab.get(t.key)?.length ?? 0) > 0);
    setTab(first?.key ?? "tied");
  }, [byTab]);

  const tabRows = byTab.get(tab) ?? [];
  const selectedKeys = useMemo(() => new Set(selected.map((r) => r.key)), [selected]);
  // Rows on this tab that can be ticked now (an unmatched row needs a choice first).
  const tickable = tabRows.filter((r) => r.op && (!needsItem(r.op) || picked[r.key]));
  const tabSelected = tickable.filter((r) => selectedKeys.has(r.key)).length;
  const allSelected = tickable.length > 0 && tabSelected === tickable.length;
  // Select all / deselect all acts on the open tab and leaves the others alone.
  const selectAllButton = (
    <button
      className="quiet"
      disabled={busy || tickable.length === 0}
      onClick={() =>
        setOn((prev) => {
          const next = new Set(prev);
          for (const r of tickable) {
            if (allSelected) next.delete(r.key); else next.add(r.key);
          }
          return next;
        })
      }
    >
      {allSelected ? "Deselect all" : "Select all"}
    </button>
  );

  const toggle = (key: string) =>
    setOn((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  async function apply() {
    if (!preview?.result) return;
    const n = selected.length;
    if (n > 0 && !window.confirm(`Apply ${n} change${n === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    setError(null);
    try {
      const operations = selected.map((r) => opFor(r, picked[r.key])).filter(Boolean);
      const res = await api.post<{ count: number; applied: Applied[]; skipped: Array<{ reason: string }> }>(
        `/sync/apply?captureId=${preview.captureId}`,
        { operations, seenListingIds: preview.result.seenListingIds }
      );
      const created = res.applied.filter((a) => a.op === "createItem");
      setDone({ count: res.count, skipped: res.skipped.map((s) => s.reason), created });
      setPreview(null);
      loadLog();
      if (created.length) void copyPhotos();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not apply those changes");
    } finally {
      setBusy(false);
    }
  }

  const s = preview?.result?.summary;
  const tabInfo = TABS.find((t) => t.key === tab)!;

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">sync</span>
        {preview && <span className="num" style={{ color: "var(--muted)" }}>{preview.platform}</span>}
      </header>

      {error && <div className="notice">{error}</div>}

      {done && (
        <div className="notice" style={{ background: "var(--pos-bg, #eef7ee)" }}>
          Applied {done.count} change{done.count === 1 ? "" : "s"}.
          {done.skipped.length > 0 && <> Skipped {done.skipped.length}: {done.skipped.join("; ")}.</>}
          {" "}See <Link to="/listings">Listed</Link> and <Link to="/sales">Sold</Link>.
        </div>
      )}

      {done && done.created.length > 0 && (
        <section className="rail">
          <h2 className="eyebrow rail-label">New items ({done.created.length})</h2>
          <p className="hint">
            Each needs its purchase price and store added. Open an item to fill them in.
          </p>
          <p className="tag-meta" style={{ display: "block" }}>
            {done.created.map((a, i) => (
              <span key={a.id}>{i > 0 && " · "}<Link to={`/items/${a.id}`}>{a.sku}</Link></span>
            ))}
          </p>
          {done.created.some((a) => !a.skuFromListing) && (
            <p className="hint">
              These got a new number, so their listings don't carry it yet. Adding "No. {"<number>"}" to the
              listing lets other sites tie by SKU (matching by title usually works without it):{" "}
              {done.created.filter((a) => !a.skuFromListing).map((a) => `No. ${a.sku?.slice(2)}`).join(", ")}
            </p>
          )}
        </section>
      )}

      {photoJob && (photoJob.running || photoJob.copied > 0) && (
        <p className="hint">
          {photoJob.running
            ? `Copying listing photos into Thrifty… ${photoJob.copied} copied, ${photoJob.remaining} item${photoJob.remaining === 1 ? "" : "s"} to go.`
            : `Copied ${photoJob.copied} listing photo${photoJob.copied === 1 ? "" : "s"}.`}
        </p>
      )}

      {busy && !preview && <p className="hint">Reading the page…</p>}

      {preview && !preview.adapterReady && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Recorded {preview.platform}</h2>
          <p className="hint">
            Thrifty can't read {preview.platform} pages yet. This recording ({preview.captures} responses
            {preview.reachedEnd ? ", scrolled to the end" : ""}) has been saved so the {preview.platform} reader
            can be built from it. Nothing in your inventory was changed.
          </p>
        </section>
      )}

      {preview?.bookmarkletOutdated && (
        <div className="notice">Your Sync bookmark is out of date. Drag the new one below to your bookmarks bar.</div>
      )}

      {s && (
        <>
          <section className="ledger">
            <div><span className="eyebrow">Listings seen</span><span className="value">{s.listingsSeen}</span></div>
            <div><span className="eyebrow">Sales seen</span><span className="value">{s.salesSeen}</span></div>
            <div><span className="eyebrow">Already right</span><span className="value">{s.tied}</span></div>

          </section>

          {!preview!.reachedEnd && s.listingsSeen > 0 && (
            <p className="hint">
              The page wasn't scrolled to the end, so listings missing from this sync aren't treated as gone.
              Press "Load everything" in the bookmark panel next time.
            </p>
          )}

          <div className="scope-tabs" role="tablist" aria-label="Sync results" style={{ flexWrap: "wrap", marginBottom: "var(--s3)" }}>
            {TABS.map((t) => {
              const list = byTab.get(t.key) ?? [];
              if (list.length === 0 && t.key !== tab) return null;
              const ticked = list.filter((r) => selectedKeys.has(r.key)).length;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  className={`scope-tab ${tab === t.key ? "active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                  <span className="num scope-count">
                    {list.some((r) => r.op) ? `${ticked}/${list.length}` : list.length}
                  </span>
                </button>
              );
            })}
          </div>

          <section className="rail" key={tab}>
              <h2 className="eyebrow rail-label">{tabInfo.label} ({tabRows.length})</h2>
              <p className="hint">{tabInfo.hint}</p>
              {tickable.length > 0 && (
                <div className="actions" style={{ marginTop: 0, alignItems: "center" }}>
                  {selectAllButton}
                  <span className="hint" style={{ margin: 0 }}>{tabSelected} of {tickable.length} selected on this tab</span>
                </div>
              )}
              {tabRows.length === 0 && <p className="hint">Nothing here for this sync.</p>}
              {tabRows.map((r) => {
                const canTick = !!r.op && (!needsItem(r.op) || !!picked[r.key]);
                const ticked = on.has(r.key) && canTick;
                return (
                  <div key={r.key} className="sale-row" style={{ opacity: r.op && !ticked ? 0.6 : 1 }}>
                    <div className="tag" style={{ cursor: "default", alignItems: "flex-start" }}>
                      {r.op ? (
                        <input
                          type="checkbox"
                          checked={ticked}
                          disabled={!canTick}
                          style={{ marginTop: "0.4rem" }}
                          onChange={() => toggle(r.key)}
                        />
                      ) : <span />}
                      <span className="tag-body">
                        <span className="tag-title">
                          {r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.title ?? r.externalId}</a> : (r.title ?? r.externalId)}
                        </span>
                        <span className="tag-meta">
                          <span>{r.kind === "sale" ? "sale" : "listing"}</span>
                          {r.brand && <span>{r.brand}</span>}
                          {r.size && <span>{r.size}</span>}
                          {r.soldAt && <span>{r.soldAt.slice(0, 10)}</span>}
                          {r.buyer && <span>{r.buyer}</span>}
                          {r.basis !== "none" && <span>{r.basis}{r.basis === "inferred" ? ` · ${Math.round(r.confidence * 100)}%` : ""}</span>}
                        </span>
                        {r.sku && (
                          <span className="tag-meta">
                            → <Link to={`/items/${r.itemId}`}>{r.sku}</Link> {r.appTitle} · {r.appStatus}
                          </span>
                        )}
                        {needsItem(r.op) && (
                          <select
                            value={picked[r.key] ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setPicked((p) => ({ ...p, [r.key]: v }));
                              setOn((prev) => {
                                const next = new Set(prev);
                                if (v) next.add(r.key); else next.delete(r.key);
                                return next;
                              });
                            }}
                            style={{ marginTop: "0.35rem", maxWidth: "100%" }}
                          >
                            <option value="">Skip</option>
                            {r.createOp && (
                              <option value="new">
                                Create new item{r.createOp.item.sku ? ` as ${r.createOp.item.sku}` : " (next SKU)"}
                              </option>
                            )}
                            {r.suggestions.map((sg) => (
                              <option key={sg.itemId} value={sg.itemId}>
                                Link to {sg.sku} · {sg.title ?? "untitled"} ({sg.score})
                              </option>
                            ))}
                          </select>
                        )}
                      </span>
                      <span className="tag-price">
                        <span className="ask">{money(r.price)}</span>
                      </span>
                    </div>

                    {r.changes.length > 0 && (
                      <div className="tag-indent">
                        {r.changes.map((c) => (
                          <div key={c.field} className="tag-meta" style={{ display: "block" }}>
                            <strong>{c.field}</strong>{" "}
                            <span className="neg">{describe(c.from, c.field)}</span> →{" "}
                            <span className="pos">{describe(c.to, c.field)}</span>
                            <span className="hint" style={{ margin: 0 }}> {c.reason}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.notes.length > 0 && (
                      <div className="tag-indent">
                        {r.notes.map((n, k) => <p key={k} className="hint" style={{ margin: "0.15rem 0" }}>⚠ {n}</p>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

          <div className="actions">
            <button onClick={() => void apply()} disabled={busy}>
              {busy ? "Applying" : selected.length ? `Apply ${selected.length} change${selected.length === 1 ? "" : "s"} (all tabs)` : "Mark synced"}
            </button>
            {selectAllButton}
            <button className="quiet" disabled={busy} onClick={() => setPreview(null)}>Discard</button>
          </div>
        </>
      )}

      <section className="rail">
        <h2 className="eyebrow rail-label">The Sync bookmark</h2>
        <p className="hint">
          Drag this to your bookmarks bar:{" "}
          <a ref={linkRef} className="button" onClick={(e) => e.preventDefault()} style={{ fontWeight: 700 }}>
            Sync to Thrifty
          </a>
        </p>
        <ol className="hint" style={{ paddingLeft: "1.2rem" }}>
          <li>Signed in to the marketplace, open the page below and click the bookmark. A small panel appears.</li>
          <li>Press <strong>Load everything</strong>; it scrolls until the whole list has loaded.</li>
          <li>Press <strong>Send</strong>. This page opens with what changed. Nothing is written until you press Apply.</li>
          <li>
            Listings that aren't in Thrifty yet become new items (with the listing's photos) when you apply.
            Sync the other sites afterwards and their listings attach to the same items, by the "No." number or
            the title.
          </li>
          <li>If Send can't reach Thrifty, press <strong>Save file</strong> and upload it here.</li>
        </ol>
        <dl className="record">
          {SITES.map((site) => (
            <div className="record-row" key={site.name}>
              <dt>
                {site.name}
                {log && !log.adapters.includes(site.name.toLowerCase()) && <span className="hint"> · recording only</span>}
              </dt>
              <dd>{site.shop}; {site.sold}</dd>
            </div>
          ))}
        </dl>
        <p className="hint">
          Upload a saved recording:{" "}
          <input
            type="file"
            accept=".json,application/json"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = "";
            }}
          />
        </p>
      </section>

      {log && log.captures.length > 0 && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Recent syncs</h2>
          <dl className="record">
            {log.captures.map((c) => (
              <div className="record-row" key={c.id}>
                <dt>
                  {c.platform} · {new Date(c.createdAt).toLocaleString()}
                  <span className="hint" style={{ display: "block", margin: 0, wordBreak: "break-all" }}>{c.pageUrl}</span>
                </dt>
                <dd className="num">
                  {c.recognized
                    ? `${c.recognized.listings} listings, ${c.recognized.sales} sales`
                    : `${c.captureCount} responses, ${Math.round(c.bytes / 1024)} KB`}
                  {c.appliedCount !== null && ` · ${c.appliedCount} applied`}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
